// Industry Benchmarking Data for ISO 42001

export const industryBenchmarks = {
  // Average compliance scores by industry
  byIndustry: {
    'Financial Services': {
      averageScore: 65,
      topPerformers: 85,
      certificationRate: 28,
      commonGaps: ['Clause 8: Operation', 'Clause 6: Planning']
    },
    'Healthcare': {
      averageScore: 58,
      topPerformers: 78,
      certificationRate: 18,
      commonGaps: ['Clause 7: Support', 'Clause 9: Performance Evaluation']
    },
    'Technology': {
      averageScore: 72,
      topPerformers: 92,
      certificationRate: 35,
      commonGaps: ['Clause 5: Leadership', 'Clause 10: Improvement']
    },
    'Retail': {
      averageScore: 48,
      topPerformers: 70,
      certificationRate: 12,
      commonGaps: ['Clause 4: Context', 'Clause 6: Planning']
    },
    'Manufacturing': {
      averageScore: 52,
      topPerformers: 75,
      certificationRate: 15,
      commonGaps: ['Clause 8: Operation', 'Clause 7: Support']
    },
    'Other': {
      averageScore: 55,
      topPerformers: 80,
      certificationRate: 20,
      commonGaps: ['Clause 4: Context', 'Clause 5: Leadership']
    }
  },
  
  // Average scores by company size
  byCompanySize: {
    'Small (1-50)': {
      averageScore: 45,
      certificationRate: 8,
      avgTimeToComplete: '6-9 months'
    },
    'Medium (51-500)': {
      averageScore: 60,
      certificationRate: 22,
      avgTimeToComplete: '9-12 months'
    },
    'Large (500+)': {
      averageScore: 70,
      certificationRate: 38,
      avgTimeToComplete: '12-18 months'
    }
  },
  
  // Certification success probability factors
  successFactors: {
    executiveSupport: { weight: 0.25, description: 'Strong leadership commitment' },
    resourceAvailability: { weight: 0.20, description: 'Dedicated team and budget' },
    existingGovernance: { weight: 0.18, description: 'Prior ISO or governance experience' },
    aiMaturity: { weight: 0.15, description: 'Current AI deployment maturity' },
    expertGuidance: { weight: 0.12, description: 'Professional consulting support' },
    organizationalReadiness: { weight: 0.10, description: 'Change management capability' }
  },
  
  // Timeline estimates based on score ranges
  timelineEstimates: {
    'Critical (0-30%)': {
      description: 'Significant work needed',
      estimatedMonths: '15-24',
      phases: ['Foundation building', 'Documentation', 'Implementation', 'Audit prep', 'Certification']
    },
    'Below Standard (31-50%)': {
      description: 'Substantial improvements required',
      estimatedMonths: '12-18',
      phases: ['Gap remediation', 'Documentation', 'Implementation', 'Audit prep', 'Certification']
    },
    'Developing (51-70%)': {
      description: 'On the right track',
      estimatedMonths: '9-12',
      phases: ['Gap closure', 'Documentation finalization', 'Audit prep', 'Certification']
    },
    'Strong (71-85%)': {
      description: 'Nearly ready',
      estimatedMonths: '6-9',
      phases: ['Fine-tuning', 'Documentation review', 'Audit prep', 'Certification']
    },
    'Excellent (86-100%)': {
      description: 'Certification ready',
      estimatedMonths: '3-6',
      phases: ['Final review', 'Audit prep', 'Certification']
    }
  },
  
  // Budget estimates (in USD)
  budgetEstimates: {
    small: {
      consulting: '15,000 - 35,000',
      certification: '8,000 - 15,000',
      implementation: '10,000 - 25,000',
      total: '33,000 - 75,000'
    },
    medium: {
      consulting: '40,000 - 80,000',
      certification: '15,000 - 30,000',
      implementation: '30,000 - 70,000',
      total: '85,000 - 180,000'
    },
    large: {
      consulting: '80,000 - 200,000',
      certification: '25,000 - 50,000',
      implementation: '70,000 - 200,000',
      total: '175,000 - 450,000'
    }
  }
};

export const getIndustryBenchmark = (industry) => {
  return industryBenchmarks.byIndustry[industry] || industryBenchmarks.byIndustry['Other'];
};

export const getCompanySizeBenchmark = (size) => {
  if (size <= 50) return industryBenchmarks.byCompanySize['Small (1-50)'];
  if (size <= 500) return industryBenchmarks.byCompanySize['Medium (51-500)'];
  return industryBenchmarks.byCompanySize['Large (500+)'];
};

export const getTimelineEstimate = (score) => {
  if (score <= 30) return industryBenchmarks.timelineEstimates['Critical (0-30%)'];
  if (score <= 50) return industryBenchmarks.timelineEstimates['Below Standard (31-50%)'];
  if (score <= 70) return industryBenchmarks.timelineEstimates['Developing (51-70%)'];
  if (score <= 85) return industryBenchmarks.timelineEstimates['Strong (71-85%)'];
  return industryBenchmarks.timelineEstimates['Excellent (86-100%)'];
};

export const getBudgetEstimate = (companySize) => {
  if (companySize <= 50) return industryBenchmarks.budgetEstimates.small;
  if (companySize <= 500) return industryBenchmarks.budgetEstimates.medium;
  return industryBenchmarks.budgetEstimates.large;
};

export const calculateSuccessProbability = (factors) => {
  // factors should be an object with keys matching successFactors
  // values should be 0-10 scores
  let totalScore = 0;
  let totalWeight = 0;
  
  Object.keys(industryBenchmarks.successFactors).forEach(key => {
    if (factors[key] !== undefined) {
      const factor = industryBenchmarks.successFactors[key];
      totalScore += (factors[key] / 10) * factor.weight;
      totalWeight += factor.weight;
    }
  });
  
  return Math.round((totalScore / totalWeight) * 100);
};

export default industryBenchmarks;
